import React, { useCallback, useEffect, useState } from 'react';
import RoutineEditor, { type RoutineEditorPayload } from './RoutineEditor';
import { RoutineService } from '../services/routineService';
import type { DailyRoutine } from '../types/routine';
import { AlertIcon } from '../../../assets/icons/index.tsx';
import { NOMBRE_DEL_DIA, ORDEN_DE_LA_SEMANA } from '../utils/semana';
import QuickGuide from '../../shared/components/QuickGuide';

const INTENSIDADES: Record<string, string> = {
  low: 'Suave',
  moderate: 'Moderada',
  high: 'Alta',
  very_high: 'Muy alta',
};

/**
 * Panel de monitores: las siete rutinas fijas de la semana.
 *
 * Cada día es **una** fila en la base de datos. Se edita la variante
 * intermedia y las de principiante y avanzado se derivan de ella al servirla,
 * así que aquí no hay que mantener tres versiones de nada.
 */
const WeeklyPlanContainer: React.FC = () => {
  const [plan, setPlan] = useState<DailyRoutine[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editando, setEditando] = useState<number | null>(null);
  const [guardado, setGuardado] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      setPlan(await RoutineService.getWeeklyPlan());
    } catch (e) {
      setPlan([]);
      setError(
        (e as Error).message.includes('403')
          ? 'Este panel es solo para monitores.'
          : 'No se ha podido cargar el plan de la semana.',
      );
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const rutinaDe = (dia: number) =>
    plan?.find((r) => r.dayOfWeek === dia) ?? null;

  const guardar = async (
    dia: number,
    payload: RoutineEditorPayload,
  ): Promise<DailyRoutine> => {
    const actualizada = await RoutineService.updateWeeklyDay(dia, payload);
    setPlan((prev) =>
      (prev ?? []).map((r) => (r.dayOfWeek === dia ? actualizada : r)),
    );
    setEditando(null);
    setGuardado(
      `${NOMBRE_DEL_DIA[dia]} actualizado. El cambio vale para todos los ${NOMBRE_DEL_DIA[dia].toLowerCase()} de aquí en adelante.`,
    );
    return actualizada;
  };

  if (editando !== null) {
    const rutina = rutinaDe(editando);
    if (!rutina) return null;
    return (
      <RoutineEditor
        initialRoutine={rutina}
        heading={NOMBRE_DEL_DIA[editando]}
        showVisibility={false}
        showNotes
        minExercises={3}
        saveLabel="Guardar el día"
        onCancel={() => setEditando(null)}
        onSave={(payload) => guardar(editando, payload)}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      <div>
        <h1 className="text-white text-xl font-semibold">Plan de la semana</h1>
        <p className="text-[#94a3b8] text-sm mt-1">
          Lo que ve el gimnasio cada día. Al cambiar un día, el cambio vale
          para ese día de la semana de aquí en adelante; los entrenos ya hechos
          guardan su propia copia y no se tocan.
        </p>
      </div>

      <QuickGuide />

      {error && (
        <div className="p-3 rounded-lg bg-red-900/40 border border-red-500/50 text-red-300 text-sm flex items-start gap-2">
          <span className="mt-0.5">
            <AlertIcon size={16} />
          </span>
          <span>{error}</span>
        </div>
      )}

      {guardado && (
        <div className="p-3 rounded-lg bg-[rgba(31,158,63,0.15)] border border-[rgba(31,158,63,0.5)] text-[#6ee06f] text-sm">
          {guardado}
        </div>
      )}

      {plan === null ? (
        <ul className="space-y-3">
          {ORDEN_DE_LA_SEMANA.map((d) => (
            <li
              key={d}
              className="h-20 bg-[#1e293b] rounded-xl border border-[#334155] animate-pulse"
            />
          ))}
        </ul>
      ) : (
        <ul className="space-y-3">
          {ORDEN_DE_LA_SEMANA.map((dia) => {
            const rutina = rutinaDe(dia);
            return (
              <li
                key={dia}
                className="bg-[#1e293b] rounded-xl border border-[#334155] p-4 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-[#64748b]">
                    {NOMBRE_DEL_DIA[dia]}
                  </p>
                  {rutina ? (
                    <>
                      <p className="text-white font-medium truncate">
                        {rutina.name}
                      </p>
                      <p className="text-[#94a3b8] text-xs mt-0.5">
                        {rutina.routineExercises.length} ejercicios ·{' '}
                        {rutina.estimatedDurationMinutes ?? '—'} min ·{' '}
                        {INTENSIDADES[rutina.intensity] ?? rutina.intensity}
                      </p>
                    </>
                  ) : (
                    <p className="text-[#fca5a5] text-sm">
                      Sin rutina. Este día la app no tiene nada que enseñar.
                    </p>
                  )}
                </div>
                {rutina && (
                  <button
                    onClick={() => {
                      setGuardado(null);
                      setEditando(dia);
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#1f9e3f] hover:opacity-90 shrink-0"
                  >
                    Editar
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-[#64748b] text-xs">
        Solo se guarda la versión intermedia. Las de principiante y avanzado se
        calculan a partir de ella: principiante hace una serie menos, descansa
        más y se salta el último ejercicio; avanzado hace una serie más y
        descansa menos. Por eso el último ejercicio de cada día conviene que
        sea un accesorio.
      </p>
    </div>
  );
};

export default WeeklyPlanContainer;
